import React, { useMemo } from 'react';
import { FL140 } from '../../../services/l204Api';
import { dateToInput, countMemoLines, getLineLimit, getApprovalLabel, isApprovalLocked } from '../helpers';

interface Tab1Props {
  formData: Partial<FL140>;
  isEditing: boolean;
  mode: string;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FL140) => FL140[keyof FL140];
  setField: (key: keyof FL140, value: FL140[keyof FL140]) => void;
  getFieldError?: (field: string) => string | undefined;
}

const Tab1BasicInfo: React.FC<Tab1Props> = ({
  formData, isEditing, mode, isFieldDisabled,
  getField, setField, getFieldError,
}) => {
  const E = (field: string) => {
    const err = getFieldError?.(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>{err}</span> : null;
  };

  // Parent document info (read-only)
  const parentDoc = formData.parent_doc;
  const approvalLabel = getApprovalLabel(parentDoc?.fl040_80);
  const locked = isApprovalLocked(parentDoc?.fl040_80);

  // Memo line count and limit (BR-C01, BR-V02)
  const memoText = (getField('fl140_21') as string) || '';
  const pageNo = (getField('fl140_02') as number) || 1;
  const lineCount = useMemo(() => countMemoLines(memoText), [memoText]);
  const lineLimit = getLineLimit(pageNo);
  const isOverLimit = lineCount > lineLimit;

  return (
    <div className="form-sections">

      {/* Approval lock warning */}
      {locked && approvalLabel && (
        <div className="approval-banner">
          <span className="approval-text">{approvalLabel}</span>
          <span className="approval-hint">文件已核准，無法修改或刪除內頁。</span>
        </div>
      )}

      {/* Section 1: 文件資訊 (from parent FL040, all readonly) */}
      <div className="form-section odd">
        <span className="section-tag">文件資訊</span>
        <div className="form-grid">
          <label className="required">文件編號{E('fl140_01')}</label>
          <input
            type="text"
            maxLength={10}
            disabled={isFieldDisabled('fl140_01')}
            value={(getField('fl140_01') as string) || ''}
            onChange={e => setField('fl140_01', e.target.value)}
          />

          <label>版次</label>
          <input
            type="text"
            readOnly
            value={parentDoc?.fl040_14 || ''}
            style={{ maxWidth: 80 }}
          />

          <label>文件名稱</label>
          <input
            type="text"
            readOnly
            value={parentDoc?.fl040_11 || ''}
          />

          <label>製訂日期</label>
          <input
            type="text"
            readOnly
            value={dateToInput(parentDoc?.fl040_12)}
            style={{ maxWidth: 120 }}
          />

          <label>修改日期</label>
          <input
            type="text"
            readOnly
            value={dateToInput(parentDoc?.fl040_13)}
            style={{ maxWidth: 120 }}
          />

          <label>總頁數</label>
          <input
            type="text"
            readOnly
            value={parentDoc?.fl040_15 != null ? String(parentDoc.fl040_15) : ''}
            style={{ maxWidth: 60 }}
          />
        </div>
      </div>

      {/* Section 2: 頁次資訊 */}
      <div className="form-section even">
        <span className="section-tag">頁次資訊</span>
        <div className="form-grid">
          <label className="required">頁次{E('fl140_02')}</label>
          <input
            type="number"
            step="0.5"
            min={1}
            disabled={isFieldDisabled('fl140_02')}
            value={getField('fl140_02') as number ?? ''}
            onChange={e => {
              let val = parseFloat(e.target.value);
              if (!isNaN(val)) {
                // BR-V01: force page number to integer or x.5
                const decimal = val - Math.floor(val);
                if (decimal > 0) {
                  val = Math.floor(val) + 0.5;
                }
                setField('fl140_02', val);
              }
            }}
            style={{ maxWidth: 100 }}
          />

          <label>章節代號{E('fl140_11')}</label>
          <input
            type="text"
            maxLength={20}
            disabled={isFieldDisabled('fl140_11')}
            value={(getField('fl140_11') as string) || ''}
            onChange={e => setField('fl140_11', e.target.value)}
          />

          <label>章節名稱{E('fl140_12')}</label>
          <input
            type="text"
            maxLength={30}
            disabled={isFieldDisabled('fl140_12')}
            value={(getField('fl140_12') as string) || ''}
            onChange={e => setField('fl140_12', e.target.value)}
          />
        </div>
      </div>

      {/* Section 3: 頁面內容 Memo (edtFl140_21) */}
      <div className="form-section odd">
        <span className="section-tag">頁面內容</span>
        <div className="memo-header">
          <span className="memo-info">
            行數: <strong className={isOverLimit ? 'over-limit' : ''}>{lineCount}</strong>
            {' / '}
            上限: {lineLimit}
          </span>
          {isOverLimit && (
            <span className="memo-warning">行數超過!</span>
          )}
          <span className="memo-hint">每頁最多55行,首頁只能45行</span>
        </div>
        <textarea
          className="memo-editor"
          disabled={isFieldDisabled('fl140_21')}
          value={(getField('fl140_21') as string) || ''}
          onChange={e => setField('fl140_21', e.target.value)}
          rows={20}
          style={{ fontFamily: 'monospace', fontSize: 13 }}
        />
        {E('fl140_21')}
      </div>
    </div>
  );
};

export default React.memo(Tab1BasicInfo);
